import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import AdminShell from "../components/AdminShell";
import { getAdminAlerts, isApiConfigured, patchAdminAlertComplete } from "../apiClient";

function formatWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function contextPreview(context) {
  if (!context || typeof context !== "object") return "";
  try {
    const s = JSON.stringify(context, null, 2);
    return s.length > 800 ? `${s.slice(0, 800)}…` : s;
  } catch {
    return "";
  }
}

export default function AdminAlertsPage({ currentUser, authToken, onSignOut }) {
  const [statusFilter, setStatusFilter] = useState("open");
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [completingId, setCompletingId] = useState("");

  const loadAlerts = useCallback(async () => {
    const t = String(authToken || "").trim();
    if (!t || !isApiConfigured()) {
      setError("Not signed in.");
      setAlerts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await getAdminAlerts(t, { status: statusFilter, limit: 100 });
      setAlerts(Array.isArray(data.alerts) ? data.alerts : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load alerts.");
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, [authToken, statusFilter]);

  useEffect(() => {
    void loadAlerts();
  }, [loadAlerts]);

  if (currentUser?.role !== "admin") {
    return <Navigate to="/admin/signin" replace />;
  }

  const handleComplete = async (alertId) => {
    const t = String(authToken || "").trim();
    if (!t || completingId) return;
    setCompletingId(alertId);
    try {
      await patchAdminAlertComplete(t, alertId, { note: "" });
      await loadAlerts();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not complete alert.");
    } finally {
      setCompletingId("");
    }
  };

  return (
    <AdminShell currentUser={currentUser} onSignOut={onSignOut}>
      <Link to="/admin" className="jw-admin__back">
        ← Back to home
      </Link>
      <h1 className="jw-admin__title">Alerts</h1>
      <div className="jw-admin__toolbar">
        <label className="jw-admin__filter-label" htmlFor="alert-status-filter">
          Status
        </label>
        <select
          id="alert-status-filter"
          className="jw-admin__filter-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="open">Open</option>
          <option value="completed">Completed</option>
        </select>
        <button type="button" className="jw-admin__refresh" onClick={() => void loadAlerts()}>
          Refresh
        </button>
      </div>

      {error ? (
        <p className="jw-admin__error" role="alert">
          {error}
        </p>
      ) : null}
      {loading ? <p className="jw-admin__muted">Loading…</p> : null}

      {!loading && alerts.length === 0 ? (
        <p className="jw-admin__muted">No {statusFilter} alerts.</p>
      ) : null}

      {!loading && alerts.length > 0 ? (
        <ul className="jw-admin-alerts">
          {alerts.map((alert) => (
            <li key={alert.id} className={`jw-admin-alert jw-admin-alert--${alert.level}`}>
              <div className="jw-admin-alert__head">
                <span className="jw-admin-alert__level">{alert.level}</span>
                <span className="jw-admin-alert__code">{alert.code}</span>
                <time className="jw-admin-alert__time" dateTime={alert.createdAt}>
                  {formatWhen(alert.createdAt)}
                </time>
              </div>
              <p className="jw-admin-alert__message">{alert.message}</p>
              <p className="jw-admin-alert__meta">
                Source: {alert.source}
                {alert.status === "completed" && alert.completedAt
                  ? ` · Completed ${formatWhen(alert.completedAt)}`
                  : ""}
              </p>
              {contextPreview(alert.context) ? (
                <pre className="jw-admin-alert__context">{contextPreview(alert.context)}</pre>
              ) : null}
              {alert.status === "open" ? (
                <button
                  type="button"
                  className="jw-admin-alert__complete"
                  disabled={completingId === alert.id}
                  onClick={() => void handleComplete(alert.id)}
                >
                  {completingId === alert.id ? "Completing…" : "Mark complete"}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </AdminShell>
  );
}
